/// <reference path="../env.d.ts" />
import { Uuid } from '../dto/common.js';
import { ApiError, ApiErrorBody, ConflictError, isApiErrorCode, NetworkError, ResponseContractError, } from '../errors.js';
import { CHANGE_BATCH_HEADER, CSRF_HEADER, CSRF_HEADER_VALUE } from '../http.js';
import { buildForm } from '../route/multipart.js';
import { buildPath, buildQueryString } from '../route/path.js';
import { successStatus } from '../route/types.js';
/** The batch a marked route's success names, or null (unmarked, absent, not a UUID). */
export function changeBatchOf(route, response) {
    if (route.changeBatch !== true)
        return null;
    const value = response.headers.get(CHANGE_BATCH_HEADER);
    return value !== null && Uuid.safeParse(value).success ? value : null;
}
/**
 * True in a production build: Vite's `import.meta.env.PROD` when it is a boolean, else
 * `NODE_ENV=production`. The arguments default to the real sources; tests pass their own.
 * `import.meta.env` stays written plainly (no cast) so that Vite's build and Vitest's
 * `vi.stubEnv` both recognize it; `src/env.d.ts` types it, loaded by the reference at the top
 * of this file so that any program compiling this file (the contract project's type-check
 * compiles only the *.test-d.ts files and their imports) sees the declaration.
 */
export function isProductionBuild(env = import.meta.env, nodeEnv = globalThis.process?.env?.NODE_ENV) {
    if (typeof env?.PROD === 'boolean')
        return env.PROD;
    return nodeEnv === 'production';
}
function errorBodyFrom(status, payload) {
    const parsed = ApiErrorBody.safeParse(payload);
    if (parsed.success)
        return parsed.data;
    const record = (typeof payload === 'object' && payload !== null ? payload : {});
    return {
        code: isApiErrorCode(record.code) ? record.code : 'internal',
        message: typeof record.message === 'string' ? record.message : `HTTP ${status}`,
    };
}
async function toError(response) {
    let payload;
    try {
        payload = await response.json();
    }
    catch {
        payload = undefined;
    }
    const body = errorBodyFrom(response.status, payload);
    if (response.status === 409)
        return new ConflictError(body);
    const status = response.status >= 400 && response.status <= 599 ? response.status : 500;
    return ApiError.fromBody(status, body);
}
async function parseSuccess(route, response, validate) {
    const expected = successStatus(route);
    if (validate && response.status !== expected) {
        throw new ResponseContractError(route.id, response.status, `expected status ${expected}, got ${response.status}`);
    }
    switch (route.response.kind) {
        case 'json': {
            const text = await response.text();
            let value;
            try {
                value = JSON.parse(text);
            }
            catch {
                throw new ResponseContractError(route.id, response.status, 'response body is not JSON');
            }
            if (validate) {
                const result = route.response.schema.safeParse(value);
                if (!result.success) {
                    throw new ResponseContractError(route.id, response.status, 'response body fails the response schema', result.error.issues);
                }
            }
            return value;
        }
        case 'empty':
            return undefined;
        case 'text':
            return response.text();
        default:
            return response.blob();
    }
}
/**
 * Make a client. Every request carries `X-Codex-Request: 1` and `credentials: 'same-origin'`;
 * path parameters are URI-encoded and the query is serialized. A multipart route sends
 * `FormData` (its fields from `input.body`, the upload from `input.file`) and never sets
 * `Content-Type` itself. A 409 throws {@link ConflictError}
 * with the parsed body, any other non-2xx {@link ApiError}, a rejected fetch {@link NetworkError}
 * (an abort rethrows the abort reason), and a success that breaks the contract
 * {@link ResponseContractError} while validation is on.
 */
export function createApiClient(options = {}) {
    const baseUrl = (options.baseUrl ?? '').replace(/\/+$/, '');
    const validate = options.validateResponses ?? !isProductionBuild();
    const doFetch = options.fetch ?? ((url, init) => globalThis.fetch(url, init));
    const call = async (route, input = {}, callOptions = {}) => {
        if (route.response.kind === 'sse') {
            throw new TypeError(`${route.id} is an SSE route: subscribe with subscribeAppEvents`);
        }
        const params = (input.params ?? {});
        const url = baseUrl + buildPath(route.path, params) + buildQueryString(input.query);
        const headers = new Headers(callOptions.headers);
        headers.set(CSRF_HEADER, CSRF_HEADER_VALUE);
        if (route.response.kind === 'json')
            headers.set('Accept', 'application/json');
        const init = { method: route.method, headers, credentials: 'same-origin' };
        if (route.requestKind === 'multipart' && route.upload !== undefined) {
            if (!(input.file instanceof Blob)) {
                throw new TypeError(`${route.id} is a multipart route: pass the upload as input.file`);
            }
            // No Content-Type: fetch writes multipart/form-data with its boundary.
            headers.delete('Content-Type');
            init.body = buildForm(route.upload.field, input.body, input.file);
        }
        else if (route.body !== undefined) {
            headers.set('Content-Type', 'application/json');
            init.body = JSON.stringify(input.body);
        }
        if (callOptions.signal !== undefined)
            init.signal = callOptions.signal;
        let response;
        try {
            response = await doFetch(url, init);
        }
        catch (error) {
            if (callOptions.signal?.aborted)
                throw error;
            throw new NetworkError(`${route.method} ${url}: no response`, { cause: error });
        }
        if (!response.ok)
            throw await toError(response);
        const value = await parseSuccess(route, response, validate);
        const batchId = callOptions.onChangeBatch === undefined ? null : changeBatchOf(route, response);
        if (batchId !== null)
            callOptions.onChangeBatch?.(batchId);
        return value;
    };
    return call;
}
/** The default client: same origin, validation on unless the build is production. */
export const api = createApiClient();
//# sourceMappingURL=api.js.map